import { AttendanceCell, CELL_STATUS_LABELS, CellStatus } from "@/lib/types";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatTimeIST } from "@/lib/timezone";
import { MessageSquareWarning } from "lucide-react";

function cellLabel(cell: AttendanceCell): string {
  if (cell.status === "L" && cell.late_minutes != null) {
    return `L${cell.late_minutes}`;
  }
  if (cell.status === "pending") return "—";
  return cell.status;
}

const statusStyles: Record<CellStatus, string> = {
  P: "bg-emerald-100 text-emerald-800 border-emerald-200",
  L: "bg-amber-100 text-amber-900 border-amber-200",
  A: "bg-red-100 text-red-800 border-red-200",
  O: "bg-sky-100 text-sky-800 border-sky-200",
  S: "bg-rose-100 text-rose-800 border-rose-200",
  TP: "bg-teal-100 text-teal-800 border-teal-200",
  MS: "bg-indigo-100 text-indigo-800 border-indigo-200",
  FS: "bg-orange-100 text-orange-900 border-orange-200",
  H: "bg-violet-100 text-violet-800 border-violet-200",
  pending: "bg-slate-100 text-slate-500 border-slate-200",
};

interface AttendanceCellBadgeProps {
  cell: AttendanceCell | null;
  showNotice?: boolean;
  showWord?: boolean;
}

export function AttendanceCellBadge({
  cell,
  showNotice = false,
  showWord = false,
}: AttendanceCellBadgeProps) {
  if (!cell) {
    return <span className="text-slate-300">·</span>;
  }

  const content = (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          "inline-flex min-w-[2rem] items-center justify-center rounded-md border px-1.5 py-0.5 text-xs font-semibold tabular-nums",
          showWord && "min-w-[2.5rem] px-2 py-1 text-sm",
          statusStyles[cell.status]
        )}
      >
        {cellLabel(cell)}
      </span>
      {showWord && (
        <span className="text-sm font-medium text-foreground">
          {CELL_STATUS_LABELS[cell.status]}
        </span>
      )}
      {showNotice && cell.has_notice && (
        <MessageSquareWarning
          className="h-3.5 w-3.5 text-amber-600"
          aria-label="Notice submitted"
        />
      )}
    </span>
  );

  const waivedNote =
    cell.status === "MS" || cell.status === "FS"
      ? cell.previous_status === "L" || cell.late_minutes
        ? `Was late${cell.late_minutes != null ? ` ${cell.late_minutes} min` : ""}`
        : "Was absent"
      : null;

  const hasTooltip =
    cell.first_punch_at ||
    cell.verify_mode ||
    cell.holiday_name ||
    cell.is_overridden ||
    waivedNote ||
    (showNotice && cell.notice_reason);

  if (!hasTooltip) return content;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="inline-flex">
            {content}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">
          {cell.first_punch_at && (
            <p>First punch: {formatTimeIST(cell.first_punch_at)}</p>
          )}
          {cell.verify_mode && (
            <p className="capitalize">Verify: {cell.verify_mode}</p>
          )}
          {cell.holiday_name && <p>Holiday: {cell.holiday_name}</p>}
          {waivedNote && <p>{waivedNote}</p>}
          {cell.is_overridden && cell.override_reason && (
            <p>Override: {cell.override_reason}</p>
          )}
          {showNotice && cell.notice_reason && (
            <p>Notice: {cell.notice_reason}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function statusColorClass(status: CellStatus): string {
  return statusStyles[status] ?? statusStyles.pending;
}

const LEGEND_ITEMS: { status: CellStatus; label: string }[] = [
  { status: "P", label: "Present" },
  { status: "L", label: "Late" },
  { status: "A", label: "Absent" },
  { status: "O", label: "Official" },
  { status: "S", label: "Sick" },
  { status: "TP", label: "Permission" },
  { status: "MS", label: "Mantras" },
  { status: "FS", label: "Fine" },
  { status: "H", label: "Holiday" },
];

export function StatusLegend({ compact = false }: { compact?: boolean }) {
  return (
    <div className={cn("flex flex-wrap gap-x-3 gap-y-1.5", compact && "text-[11px]")}>
      {LEGEND_ITEMS.map(({ status, label }) => (
        <span key={status} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            className={cn(
              "inline-flex h-5 min-w-[1.35rem] items-center justify-center rounded border px-1 text-[10px] font-semibold",
              statusStyles[status]
            )}
          >
            {status === "pending" ? "—" : status}
          </span>
          {label}
        </span>
      ))}
    </div>
  );
}
